import React, { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { FaMicrophone, FaMicrophoneSlash, FaVideo, FaVideoSlash, FaDesktop, FaComments } from 'react-icons/fa';
import { useParams, useNavigate } from 'react-router-dom';
import VideoGrid from './VideoGrid';

const socket = io(process.env.REACT_APP_API_URL); // Your backend

function Room({ roomId, userId, camOn = true, micOn = true, username }) {
  const [chat, setChat] = useState([]);
  const [msg, setMsg] = useState('');
  const [streams, setStreams] = useState({});
  const localVideo = useRef();
  const localStream = useRef();
  const [isCamOn, setIsCamOn] = useState(camOn);
  const [isMicOn, setIsMicOn] = useState(micOn);
  const [showChat, setShowChat] = useState(false);
  const displayName = username || userId;
  const [copySuccess, setCopySuccess] = useState(false);
  const navigate = useNavigate();
  const peerConnections = useRef({});
  const [hostLink, setHostLink] = useState(null);


  const roomLink = `${window.location.origin}/room/${roomId}`;
  const handleCopyLink = () => {
    navigator.clipboard.writeText(roomLink);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  



  // --- 1. Get local media on mount ---
  useEffect(() => {
    navigator.mediaDevices.getUserMedia({ video: camOn, audio: true }).then(stream => {
      localStream.current = stream;
      if (localVideo.current) localVideo.current.srcObject = stream;
      // Ensure initial media state is correct
      stream.getVideoTracks()[0].enabled = isCamOn;
      stream.getAudioTracks()[0].enabled = isMicOn;
      socket.emit('join-room', roomId, userId);
    //    socket.emit('get-room-info', roomId, (info) => {
    //   if (info && info.host === userId) {
    //     console.log("✅ I'm the host. My join link:", `${window.location.origin}/room/${roomId}`);
    //     // Here you can store it in state if you want to show it in UI
    //     // setHostLink(`${window.location.origin}/room/${roomId}`);
    //     // navigate(`/hostroom/${roomId}?host=true`);

    //   }
    // });
    });
    // Cleanup on unmount
    return () => {
      if (localStream.current) {
        localStream.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [roomId, userId]); // Simplified dependencies

  // --- 2. Handle signaling and peer connections ---
  useEffect(() => {
    const createPeer = (peerId, initiator) => {
      const pc = new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] });
      
      if (localStream.current) {
        localStream.current.getTracks().forEach(track => pc.addTrack(track, localStream.current));
      }

      pc.onicecandidate = (event) => {
        if (event.candidate) {
          socket.emit('signal', { to: peerId, from: userId, type: 'ice-candidate', candidate: event.candidate });
        }
      };

      pc.ontrack = (event) => {
        setStreams(prev => ({
            ...prev,
            [peerId]: {
                stream: event.streams[0],
                videoOn: true,
                micOn: true,
            }
        }));
      };

      if (initiator) {
        pc.onnegotiationneeded = async () => {
          try {
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);
            socket.emit('signal', { to: peerId, from: userId, type: 'offer', offer: pc.localDescription });
          } catch (err) {
            console.error("Offer creation error:", err);
          }
        };
      }
      peerConnections.current[peerId] = pc;
    };

    socket.on('user-joined', (peerId) => {
      if (peerId !== userId && !peerConnections.current[peerId]) {
        createPeer(peerId, true);
      }
    });

    socket.on('signal', async (data) => {
      const { from, type, offer, answer, candidate } = data;
      if (from === userId) return;

      let pc = peerConnections.current[from];
      if (!pc) {
        createPeer(from, false);
        pc = peerConnections.current[from];
      }

      try {
        if (type === 'offer') {
          await pc.setRemoteDescription(new RTCSessionDescription(offer));
          const ans = await pc.createAnswer();
          await pc.setLocalDescription(ans);
          socket.emit('signal', { to: from, from: userId, type: 'answer', answer: pc.localDescription });
        } else if (type === 'answer') {
          await pc.setRemoteDescription(new RTCSessionDescription(answer));
        } else if (type === 'ice-candidate') {
          await pc.addIceCandidate(new RTCIceCandidate(candidate));
        }
      } catch (err) {
        console.error(`Signal handling error:`, err);
      }
    });

    socket.on('user-left', (peerId) => {
        if (peerConnections.current[peerId]) {
            peerConnections.current[peerId].close();
            delete peerConnections.current[peerId];
        }
        setStreams(prev => {
            const newStreams = {...prev};
            delete newStreams[peerId];
            return newStreams;
        });
    });

    return () => {
      socket.off('user-joined');
      socket.off('signal');
      socket.off('user-left');
    };
  }, [roomId, userId]);

  // --- 3. Chat ---
  useEffect(() => {
    const handleChatMessage = (data) => setChat((prev) => [...prev, data]);
    socket.on('chat-message', handleChatMessage);
    return () => socket.off('chat-message', handleChatMessage);
  }, []);

  const sendMessage = () => {
    if(msg.trim()){
        const messageData = { userId: displayName, msg };
        setChat((prev) => [...prev, messageData]);
        socket.emit('chat-message', messageData);
        setMsg('');
    }
  };

  // --- 4. Media Controls & Screen Share ---
  const shareScreen = async () => {
    try {
        const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
        const videoTrack = stream.getVideoTracks()[0];
        
        Object.values(peerConnections.current).forEach(pc => {
            const sender = pc.getSenders().find(s => s.track?.kind === 'video');
            if (sender) sender.replaceTrack(videoTrack);
        });

        videoTrack.onended = async () => {
            const camStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: isMicOn });
            const camTrack = camStream.getVideoTracks()[0];
            Object.values(peerConnections.current).forEach(pc => {
                const sender = pc.getSenders().find(s => s.track?.kind === 'video');
                if (sender) sender.replaceTrack(camTrack);
            });
            if (localVideo.current) localVideo.current.srcObject = camStream;
            localStream.current = camStream;
            setIsCamOn(true);
        };

        if (localVideo.current) localVideo.current.srcObject = stream;
        localStream.current = stream;
        setIsCamOn(true);
    } catch(err) {
        console.error("Screen share failed:", err);
    }
  };

  // *** REFACTORED ***: A much more seamless and stable way to toggle media.
  const handleToggleCamera = () => {
    const videoTrack = localStream.current?.getVideoTracks()[0];
    if (videoTrack) {
      const newCamState = !videoTrack.enabled;
      videoTrack.enabled = newCamState;
      setIsCamOn(newCamState);
      socket.emit('media-status', { roomId, userId, videoOn: newCamState, micOn: isMicOn });
    }
  };

  const handleToggleMic = () => {
    const audioTrack = localStream.current?.getAudioTracks()[0];
    if (audioTrack) {
      const newMicState = !audioTrack.enabled;
      audioTrack.enabled = newMicState;
      setIsMicOn(newMicState);
      socket.emit('media-status', { roomId, userId, videoOn: isCamOn, micOn: newMicState });
    }
  };

  // Media status handler
  useEffect(() => {
    const handler = ({ userId: peerId, videoOn, micOn }) => {
      setStreams(prev => {
        if (!prev[peerId]) return prev;
        return {
            ...prev,
            [peerId]: {
              ...prev[peerId],
              videoOn,
              micOn,
            }
        };
      });
    };
    socket.on('media-status', handler);
    return () => socket.off('media-status', handler);
  }, []);

  // *** REFACTORED ***: Unified end call logic for all participants.
  const handleEndCall = () => {
  
  if (localStream.current) {
    localStream.current.getTracks().forEach(track => {
      track.stop();
      localStream.current.removeTrack(track); // remove from stream
    });
    localStream.current = null;
  }

  // If you have a video ref, clear it
  if (localVideo.current) {
    console.log("making local media off");
    localVideo.current.srcObject = null;
  }

  Object.values(peerConnections.current).forEach(pc => pc.close());
  peerConnections.current = {};

  if (socket.connected) {
    socket.disconnect();
  }

  navigate(`/thankyou`);


};



  // --- 5. UI Rendering ---
  const participants = [
    {
      id: userId,
      name: displayName + ' (You)',
      videoOn: isCamOn,
      micOn: isMicOn,
      stream: localStream.current,
      isSelf: true,
    },
    ...Object.entries(streams).map(([peerId, data]) => ({
      id: peerId,
      name: peerId,
      videoOn: data.videoOn,
      micOn: data.micOn,
      stream: data.stream,
      isSelf: false,
    }))
  ];

  return (
    <div className="min-h-screen bg-[#222] flex flex-col p-2 sm:p-4">
      <div className="bg-[#333] text-white rounded-lg px-2 sm:px-4 py-2 flex items-center mb-2" style={{ maxWidth: 1200, margin: '0 auto', width: '100%' }}>
        <img src="https://i.pravatar.cc/40" alt="avatar" className="w-8 h-8 rounded-full mr-2" />
        <span className="font-semibold text-sm sm:text-base mr-4">{displayName}</span>
        <button onClick={handleCopyLink} className="ml-auto bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-xs sm:text-sm font-semibold transition">
          Copy Room Link
        </button>
        {copySuccess && <span className="ml-2 text-green-400 text-xs">Copied!</span>}
      </div>
      
      <div className="flex-1 flex items-center justify-center" style={{ maxWidth: 1200, margin: '0 auto', width: '100%' }}>
        <VideoGrid participants={participants} localVideoRef={localVideo}/>
      </div>

      <div className="bg-[#333] rounded-lg px-2 sm:px-8 py-3 sm:py-4 flex flex-col sm:flex-row items-center justify-between mt-4 gap-3 sm:gap-0" style={{ maxWidth: 1200, margin: '0 auto', width: '100%' }}>
        <div className="text-white font-semibold text-base sm:text-lg mb-2 sm:mb-0">Class meeting</div>
        <div className="flex gap-3 sm:gap-6">
          <button onClick={handleToggleMic} className={`rounded-full p-2 sm:p-3 text-xl sm:text-2xl ${isMicOn ? 'bg-[#444] text-white' : 'bg-red-600 text-white'}`} title="Toggle Microphone">
            {isMicOn ? <FaMicrophone /> : <FaMicrophoneSlash />}
          </button>
          <button onClick={handleToggleCamera} className={`rounded-full p-2 sm:p-3 text-xl sm:text-2xl ${isCamOn ? 'bg-[#444] text-white' : 'bg-red-600 text-white'}`} title="Toggle Camera">
            {isCamOn ? <FaVideo /> : <FaVideoSlash />}
          </button>
          <button onClick={shareScreen} className="rounded-full p-2 sm:p-3 text-xl sm:text-2xl bg-[#444] text-white" title="Share Screen">
            <FaDesktop />
          </button>
          <button onClick={() => setShowChat(s => !s)} className={`rounded-full p-2 sm:p-3 text-xl sm:text-2xl ${showChat ? 'bg-yellow-400 text-black' : 'bg-[#444] text-white'}`} title="Toggle Chat">
            <FaComments />
          </button>
          <button onClick={handleEndCall} className="rounded-full p-2 sm:p-3 text-xl sm:text-2xl bg-red-700 text-white hover:bg-red-800 transition" title="End Call">
            End
          </button>
        </div>
      </div>

      {showChat && (
        <div className="bg-[#222] rounded-xl p-2 sm:p-4 mt-4" style={{ maxWidth: 500, margin: '0 auto', width: '100%' }}>
          <div className="w-full bg-[#333] rounded-xl p-2 sm:p-4 mb-2" style={{ maxHeight: 200, overflowY: 'auto' }}>
            {chat.map((c, i) => (
              <div key={i} className="text-white text-xs sm:text-base"><b>{c.userId}:</b> {c.msg}</div>
            ))}
          </div>
          <div className="flex w-full gap-2">
            <input
              value={msg}
              onChange={e => setMsg(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
              className="flex-1 rounded-full px-3 sm:px-4 py-2 border border-[#00B5D9] focus:outline-none text-xs sm:text-base"
              placeholder="Type a message..."
            />
            <button onClick={sendMessage} className="bg-[#00B5D9] text-white rounded-full px-4 sm:px-6 py-2 font-semibold hover:bg-yellow-300 hover:text-black transition text-xs sm:text-base">Send</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Room;
